'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from './ui/AppLogo';
import { LayoutDashboard, TrendingUp, FileText, Shield, AlertTriangle, DollarSign, Users, Settings, LogOut, Building2, Gavel, BarChart3, Search, Bell, Lock, ChevronRight, Activity, PieChart, Wallet, BookOpen, Flag, ClipboardCheck, Scale, Landmark, CreditCard, ArrowLeftRight, Eye, Database, UserCheck, Mail, Layers, Vault, UserCog, Compass, GitMerge, Send, ShieldAlert, Target, ScanSearch, Microscope, Hash, Download, Key, Monitor, Radio, Sliders, Cpu } from 'lucide-react';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: string | number;
  badgeVariant?: 'warning' | 'critical' | 'info';
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const portalNavConfig: Record<string, NavSection[]> = {
  investor: [
    {
      title: 'Portfolio',
      items: [
        { label: 'Dashboard', href: '/', icon: LayoutDashboard },
        { label: 'My Portfolio', href: '/', icon: PieChart },
        { label: 'Investments', href: '/', icon: TrendingUp },
        { label: 'Distributions', href: '/', icon: Wallet },
      ],
    },
    {
      title: 'Opportunities',
      items: [
        { label: 'Browse Campaigns', href: '/campaign-discovery', icon: Compass },
        { label: 'Watchlist', href: '/', icon: BookOpen },
        { label: 'Commit to Investment', href: '/investment-commitment', icon: DollarSign },
      ],
    },
    {
      title: 'Account',
      items: [
        { label: 'KYC / Suitability', href: '/kyc-submission', icon: Shield },
        { label: 'KYC Verification Status', href: '/kyc-status', icon: ClipboardCheck },
        { label: 'My Account & Compliance', href: '/investor-account', icon: UserCog },
        { label: 'Document Vault', href: '/document-vault', icon: Vault },
        { label: 'Notification Preferences', href: '/notification-preferences', icon: Bell, badge: 3, badgeVariant: 'info' },
      ],
    },
  ],
  issuer: [
    {
      title: 'Campaigns',
      items: [
        { label: 'Dashboard', href: '/issuer-portal', icon: LayoutDashboard },
        { label: 'Campaign Portal', href: '/campaign-portal', icon: Building2 },
        { label: 'My Campaigns', href: '/issuer-portal', icon: FileText },
        { label: 'Milestones', href: '/issuer-portal', icon: Flag },
      ],
    },
    {
      title: 'Investors',
      items: [
        { label: 'Investor List', href: '/issuer-portal', icon: Users },
        { label: 'Distributions', href: '/issuer-portal', icon: DollarSign },
      ],
    },
    {
      title: 'Compliance',
      items: [
        { label: 'Document Vault', href: '/issuer-portal', icon: Lock },
        { label: 'Disclosures', href: '/issuer-portal', icon: Eye },
        { label: 'Reports', href: '/issuer-portal', icon: BarChart3 },
      ],
    },
  ],
  admin: [
    {
      title: 'Platform',
      items: [
        { label: 'Dashboard', href: '/platform-administration-portal', icon: LayoutDashboard },
        { label: 'Tenants', href: '/platform-administration-portal', icon: Database },
        { label: 'Campaigns', href: '/platform-administration-portal', icon: Building2 },
        { label: 'Users', href: '/platform-administration-portal', icon: Users },
      ],
    },
    {
      title: 'User Management',
      items: [
        { label: 'User Provisioning', href: '/platform-admin-users', icon: UserCog },
        { label: 'Role Matrix', href: '/platform-admin-users', icon: Shield },
        { label: 'Permission Overrides', href: '/platform-admin-users', icon: Key, badge: 5, badgeVariant: 'warning' },
        { label: 'Session Controls', href: '/platform-admin-users', icon: Monitor, badge: 6, badgeVariant: 'info' },
      ],
    },
    {
      title: 'Security',
      items: [
        { label: 'RBAC & Roles', href: '/platform-administration-portal', icon: Shield },
        { label: 'Audit Logs', href: '/platform-administration-portal', icon: ClipboardCheck },
        { label: 'Approvals', href: '/platform-administration-portal', icon: Gavel, badge: 7, badgeVariant: 'warning' },
        { label: 'Campaign Approval', href: '/campaign-review', icon: Scale, badge: 2, badgeVariant: 'critical' },
      ],
    },
    {
      title: 'System',
      items: [
        { label: 'System Health', href: '/platform-administration-portal', icon: Activity },
        { label: 'Notifications', href: '/platform-administration-portal', icon: Bell },
        { label: 'Settings', href: '/platform-administration-portal', icon: Settings },
      ],
    },
  ],
  compliance: [
    {
      title: 'Review Queues',
      items: [
        { label: 'Dashboard', href: '/compliance-portal', icon: LayoutDashboard },
        { label: 'KYC / KYB Intake', href: '/kyc-intake', icon: UserCheck },
        { label: 'KYC/KYB Approval', href: '/kyc-approval', icon: ClipboardCheck, badge: 3, badgeVariant: 'warning' },
        { label: 'KYC/KYB Review', href: '/kyc-review', icon: Eye, badge: 6, badgeVariant: 'critical' },
        { label: 'Campaign Review', href: '/campaign-review', icon: Gavel, badge: 2, badgeVariant: 'warning' },
        { label: 'KYC / KYB Queue', href: '/compliance-portal', icon: Users, badge: 14, badgeVariant: 'warning' },
        { label: 'EDD Cases', href: '/compliance-portal', icon: Search, badge: 5, badgeVariant: 'critical' },
        { label: 'Compliance Holds', href: '/compliance-portal', icon: Lock, badge: 3, badgeVariant: 'critical' },
      ],
    },
    {
      title: 'AML / CFT',
      items: [
        { label: 'AML Monitoring', href: '/aml-monitoring', icon: ShieldAlert, badge: 9, badgeVariant: 'critical' },
        { label: 'Screening Hits', href: '/screening-hits', icon: ScanSearch, badge: 2, badgeVariant: 'critical' },
        { label: 'AML Alerts', href: '/compliance-portal', icon: AlertTriangle, badge: 9, badgeVariant: 'critical' },
        { label: 'STR / SAR Workflow', href: '/compliance-portal', icon: Flag },
        { label: 'Sanctions / PEP', href: '/compliance-portal', icon: Shield },
      ],
    },
    {
      title: 'Communications',
      items: [
        { label: 'Email Dispatch', href: '/compliance-portal', icon: Mail, badge: 3, badgeVariant: 'critical' },
        { label: 'Event Subscriptions', href: '/compliance-event-subscriptions', icon: Radio },
      ],
    },
    {
      title: 'Reporting',
      items: [
        { label: 'Regulatory Reports', href: '/compliance-portal', icon: FileText },
        { label: 'Export Suite', href: '/compliance-reporting', icon: Download },
        { label: 'Audit Trail', href: '/audit-log', icon: ClipboardCheck },
        { label: 'Config Dashboard', href: '/compliance-config', icon: Sliders },
        { label: 'Job Monitor', href: '/job-monitor', icon: Cpu },
      ],
    },
  ],
  risk: [
    {
      title: 'Risk Monitoring',
      items: [
        { label: 'Dashboard', href: '/risk-portal', icon: LayoutDashboard },
        { label: 'Risk Scores', href: '/risk-portal', icon: BarChart3 },
        { label: 'Risk Assessment', href: '/risk-assessment', icon: Microscope, badge: 3, badgeVariant: 'critical' },
        { label: 'Flagged Entities', href: '/risk-portal', icon: AlertTriangle, badge: 11, badgeVariant: 'critical' },
        { label: 'Limit Breaches', href: '/risk-portal', icon: Scale, badge: 4, badgeVariant: 'warning' },
      ],
    },
    {
      title: 'Analysis',
      items: [
        { label: 'Geographic Exposure', href: '/risk-portal', icon: Activity },
        { label: 'Portfolio Risk', href: '/risk-portal', icon: PieChart },
        { label: 'Concentration', href: '/risk-portal', icon: TrendingUp },
      ],
    },
    {
      title: 'Reporting',
      items: [
        { label: 'Risk Reports', href: '/risk-portal', icon: FileText },
        { label: 'Audit Trail', href: '/risk-portal', icon: ClipboardCheck },
      ],
    },
  ],
  finance: [
    {
      title: 'Funds Management',
      items: [
        { label: 'Dashboard', href: '/finance-portal', icon: LayoutDashboard },
        { label: 'Escrow Positions', href: '/finance-portal', icon: Landmark },
        { label: 'Settlement Queue', href: '/finance-portal', icon: ArrowLeftRight, badge: 8, badgeVariant: 'warning' },
        { label: 'Settlement Processor', href: '/settlement-processor', icon: Layers, badge: 2, badgeVariant: 'critical' },
        { label: 'Distributions', href: '/distribution-management', icon: Send, badge: 3, badgeVariant: 'warning' },
      ],
    },
    {
      title: 'Ledger',
      items: [
        { label: 'Transaction Ledger', href: '/finance-portal', icon: BookOpen },
        { label: 'Double-Entry Ledger', href: '/ledger-viewer', icon: Target },
        { label: 'Reconciliation', href: '/reconciliation', icon: GitMerge, badge: 4, badgeVariant: 'critical' },
        { label: 'Fee Management', href: '/finance-portal', icon: CreditCard },
      ],
    },
    {
      title: 'Reporting',
      items: [
        { label: 'Financial Reports', href: '/finance-portal', icon: FileText },
        { label: 'Audit Trail', href: '/finance-portal', icon: Shield },
      ],
    },
  ],
  trustee: [
    {
      title: 'Oversight',
      items: [
        { label: 'Portfolio Oversight', href: '/trustee-portal', icon: PieChart },
        { label: 'Escrow Oversight', href: '/trustee-portal', icon: Landmark, badge: 1, badgeVariant: 'warning' },
        { label: 'Settlement Review', href: '/trustee-portal', icon: ArrowLeftRight, badge: 3, badgeVariant: 'warning' },
        { label: 'Custody Verification', href: '/trustee-portal', icon: Shield, badge: 1, badgeVariant: 'critical' },
      ],
    },
    {
      title: 'Documents',
      items: [
        { label: 'Document Vault', href: '/document-vault', icon: Vault },
        { label: 'Audit Trail', href: '/audit-log', icon: ClipboardCheck },
      ],
    },
  ],
  auditor: [
    {
      title: 'Oversight',
      items: [
        { label: 'Auditor Portal', href: '/auditor-portal', icon: Eye },
        { label: 'Transaction Logs', href: '/auditor-portal', icon: BookOpen },
        { label: 'Settlement Verification', href: '/auditor-portal', icon: ArrowLeftRight, badge: 2, badgeVariant: 'warning' },
        { label: 'Compliance Controls', href: '/auditor-portal', icon: ClipboardCheck, badge: 1, badgeVariant: 'critical' },
        { label: 'Cryptographic Proofs', href: '/auditor-portal', icon: Hash },
      ],
    },
    {
      title: 'Ledger',
      items: [
        { label: 'Double-Entry Ledger', href: '/ledger-viewer', icon: Target },
        { label: 'Reconciliation', href: '/reconciliation', icon: GitMerge, badge: 4, badgeVariant: 'critical' },
      ],
    },
    {
      title: 'Documents',
      items: [
        { label: 'Document Vault', href: '/document-vault', icon: Vault },
        { label: 'Audit Trail', href: '/audit-log', icon: ClipboardCheck },
      ],
    },
  ],
};

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  portalRole: string;
}

export default function Sidebar({ collapsed, mobileOpen, onMobileClose, portalRole }: SidebarProps) {
  const pathname = usePathname();
  const navSections = portalNavConfig[portalRole] || portalNavConfig.investor;

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const badgeClass = (variant?: string) => {
    if (variant === 'critical') return 'bg-critical/20 text-critical text-xs font-mono-data px-1.5 py-0.5 rounded-full';
    if (variant === 'warning') return 'bg-warning/20 text-warning text-xs font-mono-data px-1.5 py-0.5 rounded-full';
    return 'bg-info/20 text-info text-xs font-mono-data px-1.5 py-0.5 rounded-full';
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-surface-2 border-r border-border transition-all duration-300 ease-in-out flex-shrink-0 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 py-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-bold text-base text-foreground tracking-tight">CrowdVault™</span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {navSections.map((section) => (
            <div key={`section-${section.title}`} className="mb-4">
              {!collapsed && (
                <p className="section-header">{section.title}</p>
              )}
              {section.items.map((item) => (
                <Link
                  key={`nav-${item.href}-${item.label}`}
                  href={item.href}
                  className={`sidebar-nav-item group relative ${isActive(item.href) ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon size={18} className="flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{item.label}</span>
                      {item.badge !== undefined && (
                        <span className={badgeClass(item.badgeVariant)}>{item.badge}</span>
                      )}
                    </>
                  )}
                  {collapsed && item.badge !== undefined && (
                    <span className="absolute top-0 right-0 w-2 h-2 rounded-full bg-critical" />
                  )}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className={`border-t border-border p-2 flex flex-col gap-1 ${collapsed ? 'items-center' : ''}`}>
          <button className={`sidebar-nav-item w-full ${collapsed ? 'justify-center' : ''}`} title={collapsed ? 'Settings' : undefined}>
            <Settings size={18} />
            {!collapsed && <span>Settings</span>}
          </button>
          <button className={`sidebar-nav-item w-full text-negative/80 hover:text-negative ${collapsed ? 'justify-center' : ''}`} title={collapsed ? 'Sign Out' : undefined}>
            <LogOut size={18} />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-surface-2 border-r border-border flex flex-col lg:hidden transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-sm text-foreground">CrowdVault™</span>
          </div>
          <button onClick={onMobileClose} className="btn-ghost p-1">
            <ChevronRight size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {navSections.map((section) => (
            <div key={`mobile-section-${section.title}`} className="mb-4">
              <p className="section-header">{section.title}</p>
              {section.items.map((item) => (
                <Link
                  key={`mobile-nav-${item.label}`}
                  href={item.href}
                  onClick={onMobileClose}
                  className={`sidebar-nav-item ${isActive(item.href) ? 'active' : ''}`}
                >
                  <item.icon size={18} />
                  <span className="flex-1">{item.label}</span>
                  {item.badge !== undefined && (
                    <span className={badgeClass(item.badgeVariant)}>{item.badge}</span>
                  )}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-2">
          <button className="sidebar-nav-item w-full text-negative/80 hover:text-negative">
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
}